import * as React from "react";
import { Typography, Box, Chip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../../../assets/styles/tokens";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";
import { Stack } from "@mui/system";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

const xData = [
  "08 Oct",
  "11 Oct",
  "14 Oct",
  "17 Oct",
  "20 Oct",
  "23 Oct",
  "26 Oct",
];

const yData = [20, 55, 40, 85, 100, 110, 120];

const chartData = xData.map((x, i) => ({ x, value: yData[i] }));

const notes = [2, 4];

// ✅ Custom dot — hollow normally, filled + enlarged when active
const CustomDot = (props: any) => {
  const { cx, cy, index, activeIndex, color } = props;
  if (cx === undefined || cy === undefined) return null;
  const isActive = activeIndex === index;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isActive ? 5 : 3}
      fill={isActive ? color : "white"}
      stroke={isActive ? "white" : color}
      strokeWidth={isActive ? 1 : 1}
      style={
        isActive
          ? { filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.25))" }
          : undefined
      }
    />
  );
};

// ✅ Custom X axis tick — day and month on separate lines
const CustomXTick = (props: any) => {
  const { x, y, payload } = props;
  const [day, month] = payload.value.split(" ");
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={colors.GrayLightMode[600].$value}
      fontSize={typography.Size.Text.xxs.$value}
      fontWeight={typography.Font_weight.Medium.$value}
    >
      <tspan x={x} dy="1em">
        {day}
      </tspan>
      <tspan x={x} dy="1.5em"> 
        {month}
      </tspan>
    </text>
  );
};

export default function CACActionChart() {
  const [activeNote, setActiveNote] = React.useState<number | null>(null);
  const [showFullNote, setShowFullNote] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);

  const seriesColor = colors.Brand[600].$value;

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const resize = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    resize.observe(chartRef.current);
    return () => resize.disconnect();
  }, []);

  const handleChartClick = (rechartsData: any) => {
    if (!rechartsData || rechartsData.activeTooltipIndex === undefined) {
      setActiveIndex(null);
      return;
    }
    const index = rechartsData.activeTooltipIndex;
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const handleNoteClick = (index: number) => {
    setActiveNote((prev) => (prev === index ? null : index));
  };

  const getNoteLeft = (index: number) => {
    const marginLeft = 48;
    const marginRight = 24;
    const usableWidth = chartWidth - marginLeft - marginRight;
    const step = usableWidth / (chartData.length - 1);
    return marginLeft + step * index;
  };

  return (
    <>
      {/* Chart */}
      <Box ref={chartRef} sx={{ width: "100%", position: "relative" }}>
        <ResponsiveContainer width="100%" height={242}>
          <AreaChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
            onClick={handleChartClick}
            style={{ cursor: "pointer" }}
          >
            <defs>
              <linearGradient
                id="impressionGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={seriesColor} stopOpacity={0.5} />
                <stop
                  offset="100%"
                  stopColor={colors.Brand[50].$value}
                  stopOpacity={0.09}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray=""
              vertical={false}
              stroke={colors.GrayLightMode[300].$value}
              opacity={0.3}
            />

            <XAxis
              dataKey="x"
              tick={<CustomXTick />}
              tickLine={false}
              axisLine={false}
              interval={0}
              height={48}
              tickMargin={10}
            />

            <YAxis
              domain={[20, 120]}
              ticks={[20, 40, 60, 80, 100, 120]}
              tick={{
                fill: colors.GrayLightMode[600].$value,
                fontSize: typography.Size.Text.xxs.$value,
                fontWeight: typography.Font_weight.Medium.$value,
              }}
              tickLine={false}
              axisLine={false}
              width={35}
              tickMargin={3}
            />

            <Tooltip
              contentStyle={{
                borderRadius: spacing.Radius.xs.$value,
                border: "none",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                fontSize: typography.Size.Text.xs.$value,
                padding: "4px",
                display: "flex",
                fontWeight: typography.Font_weight.Bold.$value,
              }}
              cursor={{
                stroke: colors.GrayLightMode[200].$value,
                strokeWidth: 1,
              }}
              labelFormatter={() => ""}
              formatter={(value) => [`${value}%`]}
              itemStyle={{ padding: 0 }}
              separator=""
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke={seriesColor}
              strokeWidth={1.5}
              fill="url(#impressionGradient)"
              dot={(dotProps: any) => (
                <CustomDot
                  {...dotProps}
                  activeIndex={activeIndex}
                  color={seriesColor}
                />
              )}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Note Icons */}
        {notes.map((index) => (
          <IconButton
            key={index}
            size="small"
            sx={{
              position: "absolute",
              bottom: 0,
              marginLeft:'-3px',
              left: getNoteLeft(index),
              transform: "translateX(-50%)",
            }}
            onClick={() => handleNoteClick(index)}
          >
            <StickyNote2OutlinedIcon sx={{ width: "14px" }} />
          </IconButton>
        ))}
      </Box>

      {/* Collapse note panel */}
      <Collapse in={activeNote !== null}>
        <Box
          sx={{
            display: "flex",
            px: 1.5,
            py: 1,
            gap: 0.5,
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Stack flexDirection="row" gap={0.5} alignItems="center">
            <Avatar sx={{ width: 18, height: 18 }} src="#" />
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              lineHeight={typography.Line_height.text.xs.$value}
              fontWeight={typography.Font_weight.Regular.$value}
              color={colors.GrayLightMode[700].$value}
            >
              Netanel Dahan
            </Typography>
            <Chip
              label="Product & Services"
              variant="outlined"
              size="small"
              sx={{
                height: spacing.Spacing["1xl"].$value,
                fontSize: typography.Size.Text.xxs.$value,
                lineHeight: typography.Line_height.text.xxs.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                color: colors.Brand[700].$value,
                borderColor: colors.Brand[700].$value,
                borderRadius: spacing.Radius["2xl"].$value,
                ml: 0.5,
              }}
            />
          </Stack>

          <Box>
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              color={colors.GrayLightMode[900].$value}
              fontWeight={typography.Font_weight.Semibold.$value}
            >
              Bing - Bingo main 2 tcpa & bingo main 2[bingo] tier3 tcpa & bingo
              uk bonus tcp - Into one ad group
            </Typography>
            <Collapse in={showFullNote}>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                color={colors.GrayLightMode[900].$value}
                fontWeight={typography.Font_weight.Semibold.$value}
                mt={0.5}
              >
                This is the complete note text. Here you can show the full
                description of the note including campaign details, targeting,
                ad group adjustments, or any additional commentary related to
                this chart point.
              </Typography>
            </Collapse>
            <Typography
              fontSize={typography.Size.Text.xs.$value}
              fontWeight={typography.Font_weight.Medium.$value}
              color={colors.Brand[700].$value}
              mt={0.5}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setShowFullNote((prev) => !prev)}
            >
              {showFullNote ? "Hide full Note" : "See full Note"}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </>
  );
}
